import React, { useState, useEffect, useCallback } from 'react';
import { sourcesApi, demoApi } from '../api';
import StatusBadge from '../shared/StatusBadge';
import ConfirmDialog from '../shared/ConfirmDialog';
import JsonViewer from '../shared/JsonViewer';

const SOURCE_TYPES = ['postgresql', 'mysql', 'mssql', 'mongodb', 'cassandra', 'kafka'];
const CDC_MODES = ['polling', 'logical_replication', 'change_stream', 'debezium', 'webhook'];

const EMPTY_FORM = {
  workspace_id: '',
  name: '',
  source_type: 'postgresql',
  connection_uri: '',
  schema_filter: '',
  cdc_mode: 'polling',
  polling_interval_ms: 5000,
};

export default function SourcesTab({ workspaceId }) {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM, workspace_id: workspaceId });
  const [expandedSource, setExpandedSource] = useState(null);
  const [schemaData, setSchemaData] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [addSourceError, setAddSourceError] = useState(null);
  const [addSourceChecking, setAddSourceChecking] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editUriModal, setEditUriModal] = useState(null);
  const [editConnUri, setEditConnUri] = useState('');
  const [editUriLoading, setEditUriLoading] = useState(false);
  const [editUriFetching, setEditUriFetching] = useState(null);
  const [editUriError, setEditUriError] = useState(null);
  const [bootstrapBusy, setBootstrapBusy] = useState(false);
  const [bootstrapResult, setBootstrapResult] = useState(null);

  const loadSources = useCallback(async () => {
    setLoading(true);
    try {
      const data = await sourcesApi.list(workspaceId);
      setSources(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load sources:', err);
    }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { loadSources(); }, [loadSources]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const r = await sourcesApi.readiness();
      if (!r.ready) {
        setAddSourceError(r.message || 'Cannot save this data source right now.');
        return;
      }
      const connectionUri =
        (form.connection_uri || '').trim() || r.suggested_postgresql_uri || '';
      if (!connectionUri) {
        setAddSourceError('Connection URI is required.');
        return;
      }
      const payload = {
        ...form,
        workspace_id: workspaceId,
        connection_uri: connectionUri,
        schema_filter: form.schema_filter ? form.schema_filter.split(',').map(s => s.trim()) : null,
      };
      await sourcesApi.create(payload);
      setShowForm(false);
      setAddSourceError(null);
      setForm({ ...EMPTY_FORM, workspace_id: workspaceId });
      await loadSources();
    } catch (err) {
      alert('Error creating source: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleIntrospect = async (sourceId) => {
    setActionLoading(sourceId + '_introspect');
    try {
      const result = await sourcesApi.introspect(sourceId);
      alert(`Introspected ${result.tables} tables, ${result.relationships} relationships. Changes: ${result.schema_changes?.length || 0}`);
      loadSources();
    } catch (err) {
      alert('Introspect error: ' + (err.response?.data?.detail || err.message));
    }
    setActionLoading('');
  };

  const handleAutoInfer = async (sourceId) => {
    setActionLoading(sourceId + '_infer');
    try {
      const result = await sourcesApi.autoInfer(sourceId);
      alert(`Inferred ${result.inferred} tags (${result.total} total after merge)`);
    } catch (err) {
      alert('Auto-infer error: ' + (err.response?.data?.detail || err.message));
    }
    setActionLoading('');
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await sourcesApi.delete(deleteId);
      setDeleteId(null);
      loadSources();
    } catch (err) {
      alert('Delete error: ' + (err.response?.data?.detail || err.message));
      setDeleteId(null);
    }
  };

  const openEditConnectionUri = async (sourceId) => {
    setEditUriError(null);
    setEditUriModal(null);
    setEditConnUri('');
    setEditUriFetching(sourceId);
    try {
      const d = await sourcesApi.get(sourceId);
      setEditUriModal({ sourceId, name: d.name || '' });
      setEditConnUri(d.connection_uri || '');
    } catch (err) {
      alert('Could not load source: ' + (err.response?.data?.detail || err.message || 'Unknown error'));
    } finally {
      setEditUriFetching(null);
    }
  };

  const closeEditUriModal = () => {
    setEditUriModal(null);
    setEditConnUri('');
    setEditUriError(null);
  };

  const saveEditConnectionUri = async (e) => {
    e.preventDefault();
    if (!editUriModal) return;
    setEditUriError(null);
    setEditUriLoading(true);
    try {
      await sourcesApi.updateSource(editUriModal.sourceId, { connection_uri: editConnUri });
      closeEditUriModal();
      await loadSources();
    } catch (err) {
      setEditUriError(err.response?.data?.detail || err.message || 'Update failed.');
    } finally {
      setEditUriLoading(false);
    }
  };

  const handleViewSchema = async (sourceId) => {
    if (expandedSource === sourceId) {
      setExpandedSource(null);
      setSchemaData(null);
      return;
    }
    setActionLoading(sourceId + '_schema');
    try {
      const data = await sourcesApi.getSchema(sourceId);
      setSchemaData({ ...data, source_id: sourceId });
      setExpandedSource(sourceId);
    } catch (err) {
      alert('Schema not available. Run introspect first.');
    }
    setActionLoading('');
  };

  const refreshSchemaFor = async (sourceId) => {
    try {
      const data = await sourcesApi.getSchema(sourceId);
      setSchemaData({ ...data, source_id: sourceId });
    } catch (err) {
      console.error('Failed to refresh schema after tag save:', err);
    }
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const toggleAddSourceForm = async () => {
    if (showForm) {
      setShowForm(false);
      setAddSourceError(null);
      return;
    }
    setAddSourceChecking(true);
    setAddSourceError(null);
    try {
      const r = await sourcesApi.readiness();
      if (!r.ready) {
        setAddSourceError(r.message || 'Cannot add a data source right now.');
        return;
      }
      setForm((prev) => ({
        ...EMPTY_FORM,
        workspace_id: workspaceId,
        connection_uri: r.suggested_postgresql_uri || prev.connection_uri || '',
      }));
      setShowForm(true);
    } catch (err) {
      setAddSourceError(
        err.response?.data?.detail
          || err.message
          || 'Could not reach GraphBuilder to verify the database. Check that the service is running.',
      );
    } finally {
      setAddSourceChecking(false);
    }
  };

  const handleBootstrap = async () => {
    if (!workspaceId) { alert('Select a workspace first.'); return; }
    setBootstrapBusy(true);
    setBootstrapResult(null);
    try {
      const res = await demoApi.bootstrap({ workspace_id: workspaceId });
      setBootstrapResult(res);
      setShowForm(false);
      await loadSources();
    } catch (err) {
      alert('Bootstrap failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setBootstrapBusy(false);
    }
  };

  return (
    <div>
      <div className="ednl-card">
        <div className="ednl-card__header">
          <h2 className="ednl-card__title">Data Sources</h2>
          <button
            type="button"
            className="ednl-btn ednl-btn--primary"
            onClick={toggleAddSourceForm}
            disabled={addSourceChecking}
          >
            {addSourceChecking ? 'Checking…' : showForm ? 'Cancel' : '+ Add Source'}
          </button>
        </div>
        {addSourceError && (
          <div className="ednl-inline-alert ednl-inline-alert--error" role="alert">
            {addSourceError}
          </div>
        )}

        {!loading && sources.length === 0 && !showForm && (
          <div className="ednl-demo-card">
            <div className="ednl-demo-card__icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
              </svg>
            </div>
            <div className="ednl-demo-card__body">
              <h3 className="ednl-demo-card__title">Banking AML Demo</h3>
              <p className="ednl-demo-card__desc">
                Provisions a banking database with customer transactions, installs AML fraud-detection
                patterns (structuring, wire velocity, geo spread), and creates frame accumulation rules
                — everything needed to run the full EDNL pipeline.
              </p>
              {bootstrapResult && (
                <div className="ednl-inline-alert ednl-inline-alert--success" style={{ marginTop: 8, marginBottom: 0 }}>
                  Source created &middot; {bootstrapResult.transactions_seeded} transactions seeded &middot;{' '}
                  {bootstrapResult.patterns_installed} patterns installed &middot; Frame rule active.
                  Head to <a href="/user-home/ednl-demo" style={{ fontWeight: 600 }}>EDNL Demo</a> to replay the pipeline.
                </div>
              )}
            </div>
            <button
              className="ednl-btn ednl-btn--primary"
              onClick={handleBootstrap}
              disabled={bootstrapBusy}
              style={{ whiteSpace: 'nowrap', alignSelf: 'center' }}
            >
              {bootstrapBusy ? 'Setting up…' : 'Set Up Demo'}
            </button>
          </div>
        )}

        {showForm && (
          <form className="ednl-form" onSubmit={handleCreate} style={{ marginBottom: 20 }}>
            <div className="ednl-form-row">
              <div className="ednl-form-group">
                <label className="ednl-label">Name</label>
                <input className="ednl-input" value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
              <div className="ednl-form-group">
                <label className="ednl-label">Type</label>
                <select className="ednl-select" value={form.source_type} onChange={e => set('source_type', e.target.value)}>
                  {SOURCE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="ednl-form-group">
                <label className="ednl-label">CDC Mode</label>
                <select className="ednl-select" value={form.cdc_mode} onChange={e => set('cdc_mode', e.target.value)}>
                  {CDC_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="ednl-form-row">
              <div className="ednl-form-group" style={{ flex: 3 }}>
                <label className="ednl-label">Connection URI</label>
                <input
                  className="ednl-input"
                  value={form.connection_uri}
                  onChange={e => set('connection_uri', e.target.value)}
                  placeholder="postgresql://USER:PASSWORD@host.docker.internal:6432/DATABASE"
                  required
                />
                <p className="ednl-hint" style={{ marginTop: 6, lineHeight: 1.45 }}>
                  If GraphBuilder runs in Docker, <code style={{ fontSize: '0.95em' }}>localhost</code> in this URI refers to the container, not your PC.
                  Use <code style={{ fontSize: '0.95em' }}>host.docker.internal</code> (Docker Desktop on Windows/Mac) to reach PostgreSQL on the host; try port 6432 (PgBouncer) or 5432 (Postgres).
                </p>
              </div>
              <div className="ednl-form-group">
                <label className="ednl-label">Schema Filter (comma-separated)</label>
                <input className="ednl-input" value={form.schema_filter} onChange={e => set('schema_filter', e.target.value)} placeholder="public, sales" />
              </div>
            </div>
            <div>
              <button className="ednl-btn ednl-btn--primary" type="submit">Create Source</button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="ednl-loading">Loading sources...</div>
        ) : sources.length === 0 ? (
          <div className="ednl-empty">
            <div className="ednl-empty__icon">🔌</div>
            <div className="ednl-empty__text">No data sources registered yet</div>
          </div>
        ) : (
          <div className="ednl-table-wrap">
            <table className="ednl-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>CDC Mode</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => (
                  <React.Fragment key={s.source_id}>
                    <tr>
                      <td>{s.name}</td>
                      <td><StatusBadge status="info" label={s.source_type} /></td>
                      <td>{s.cdc_mode}</td>
                      <td><StatusBadge status={s.enabled ? 'active' : 'inactive'} label={s.enabled ? 'Enabled' : 'Disabled'} /></td>
                      <td>
                        <div className="ednl-btn-group">
                          <button className="ednl-btn ednl-btn--sm" onClick={() => handleIntrospect(s.source_id)} disabled={actionLoading === s.source_id + '_introspect'}>
                            {actionLoading === s.source_id + '_introspect' ? 'Working...' : 'Introspect'}
                          </button>
                          <button className="ednl-btn ednl-btn--sm" onClick={() => handleAutoInfer(s.source_id)} disabled={actionLoading === s.source_id + '_infer'}>
                            {actionLoading === s.source_id + '_infer' ? 'Inferring...' : 'Auto-Infer Tags'}
                          </button>
                          <button className="ednl-btn ednl-btn--sm" onClick={() => handleViewSchema(s.source_id)} disabled={actionLoading === s.source_id + '_schema'}>
                            {expandedSource === s.source_id ? 'Hide Schema' : 'View Schema'}
                          </button>
                          <button
                            type="button"
                            className="ednl-btn ednl-btn--sm"
                            onClick={() => openEditConnectionUri(s.source_id)}
                            disabled={editUriFetching === s.source_id}
                          >
                            {editUriFetching === s.source_id ? 'Loading…' : 'Edit URI'}
                          </button>
                          <button
                            type="button"
                            className="ednl-btn ednl-btn--sm ednl-btn--danger"
                            onClick={() => setDeleteId(s.source_id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedSource === s.source_id && schemaData && (
                      <tr className="ednl-expand-row">
                        <td colSpan={5}>
                          <SchemaViewer data={schemaData} onTagsUpdated={refreshSchemaFor} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog open={!!deleteId} title="Delete Source" message="Permanently delete this data source and its schema/tags?" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />

      {editUriModal && (
        <div className="ednl-dialog-overlay" role="presentation" onClick={editUriLoading ? undefined : closeEditUriModal}>
          <div className="ednl-dialog" style={{ maxWidth: 560 }} role="dialog" aria-labelledby="ednl-edit-uri-title" onClick={(ev) => ev.stopPropagation()}>
            <h3 id="ednl-edit-uri-title" className="ednl-dialog__title">
              Edit connection URI{editUriModal.name ? ` — ${editUriModal.name}` : ''}
            </h3>
            {editUriError && (
              <div className="ednl-inline-alert ednl-inline-alert--error" role="alert" style={{ marginBottom: 12 }}>
                {editUriError}
              </div>
            )}
            <form onSubmit={saveEditConnectionUri}>
              <label className="ednl-label" htmlFor="ednl-edit-conn-uri">Connection URI</label>
              <textarea
                id="ednl-edit-conn-uri"
                className="ednl-textarea"
                rows={4}
                style={{ width: '100%', minHeight: 88 }}
                value={editConnUri}
                onChange={(e) => setEditConnUri(e.target.value)}
                disabled={editUriLoading}
                required
              />
              <p className="ednl-hint" style={{ marginTop: 8 }}>
                From Docker, use host.docker.internal (not localhost) to reach the database on your machine.
              </p>
              <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="ednl-btn" onClick={closeEditUriModal} disabled={editUriLoading}>
                  Cancel
                </button>
                <button type="submit" className="ednl-btn ednl-btn--primary" disabled={editUriLoading}>
                  {editUriLoading ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const SEMANTIC_TYPE_OPTIONS = [
  'unknown',
  'entity_id',
  'entity_name',
  'entity_attribute',
  'event_type',
  'event_timestamp',
  'event_value',
  'relationship_fk',
  'metric',
  'status',
  'category',
  'free_text',
];

const NARRATIVE_ROLE_OPTIONS = [
  'subject',
  'verb',
  'object',
  'modifier',
  'context',
  'identifier',
];

const DATA_CLASSIFICATION_OPTIONS = [
  'public',
  'internal',
  'confidential',
  'phi',
  'pii',
];

const VOCAB_ELIGIBLE_TYPES = new Set(['status', 'category']);

function _tagToEditState(tag, col, fieldPath, sourceId) {
  const vocab = tag?.value_vocabulary && typeof tag.value_vocabulary === 'object'
    ? Object.entries(tag.value_vocabulary).map(([k, v]) => ({ key: String(k), value: String(v) }))
    : [];
  return {
    source_id: tag?.source_id || sourceId,
    field_path: fieldPath,
    business_name: tag?.business_name || col?.name || '',
    business_description: tag?.business_description || '',
    semantic_type: tag?.semantic_type || 'unknown',
    narrative_role: tag?.narrative_role || 'context',
    data_classification: tag?.data_classification || 'internal',
    entity_type: tag?.entity_type || '',
    vocab_rows: vocab.length > 0 ? vocab : [],
  };
}

function TagEditorModal({ initial, columnName, onClose, onSave, saving, error }) {
  const [form, setForm] = useState(initial);

  const showVocab = VOCAB_ELIGIBLE_TYPES.has(form.semantic_type);

  const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const addVocabRow = () => setForm((f) => ({
    ...f,
    vocab_rows: [...(f.vocab_rows || []), { key: '', value: '' }],
  }));
  const removeVocabRow = (idx) => setForm((f) => ({
    ...f,
    vocab_rows: (f.vocab_rows || []).filter((_, i) => i !== idx),
  }));
  const updateVocabRow = (idx, field, value) => setForm((f) => ({
    ...f,
    vocab_rows: (f.vocab_rows || []).map((row, i) => (i === idx ? { ...row, [field]: value } : row)),
  }));

  const handleSubmit = (e) => {
    e.preventDefault();
    let vocab = null;
    if (showVocab && (form.vocab_rows || []).length > 0) {
      vocab = {};
      for (const row of form.vocab_rows) {
        const k = (row.key || '').trim();
        if (!k) continue;
        vocab[k] = row.value || '';
      }
      if (Object.keys(vocab).length === 0) vocab = null;
    }
    const payload = {
      source_id: form.source_id,
      field_path: form.field_path,
      business_name: (form.business_name || '').trim() || columnName,
      business_description: (form.business_description || '').trim(),
      semantic_type: form.semantic_type || 'unknown',
      narrative_role: form.narrative_role || 'context',
      data_classification: form.data_classification || 'internal',
      entity_type: (form.entity_type || '').trim() || null,
      value_vocabulary: vocab,
    };
    onSave(payload);
  };

  return (
    <div className="ednl-dialog-overlay" role="presentation" onClick={saving ? undefined : onClose}>
      <div
        className="ednl-dialog"
        style={{ maxWidth: 640 }}
        role="dialog"
        aria-labelledby="ednl-tag-editor-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        <h3 id="ednl-tag-editor-title" className="ednl-dialog__title">
          Edit semantic tag — <span className="mono">{form.field_path}</span>
        </h3>
        {error && (
          <div className="ednl-inline-alert ednl-inline-alert--error" role="alert" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="ednl-label">Business name</label>
              <input
                className="ednl-input"
                value={form.business_name}
                onChange={(e) => updateField('business_name', e.target.value)}
                placeholder={columnName}
                disabled={saving}
              />
            </div>
            <div>
              <label className="ednl-label">Semantic type</label>
              <select
                className="ednl-input"
                value={form.semantic_type}
                onChange={(e) => updateField('semantic_type', e.target.value)}
                disabled={saving}
              >
                {SEMANTIC_TYPE_OPTIONS.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="ednl-label">Narrative role</label>
              <select
                className="ednl-input"
                value={form.narrative_role}
                onChange={(e) => updateField('narrative_role', e.target.value)}
                disabled={saving}
              >
                {NARRATIVE_ROLE_OPTIONS.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="ednl-label">Data classification</label>
              <select
                className="ednl-input"
                value={form.data_classification}
                onChange={(e) => updateField('data_classification', e.target.value)}
                disabled={saving}
              >
                {DATA_CLASSIFICATION_OPTIONS.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div style={{ gridColumn: '1 / span 2' }}>
              <label className="ednl-label">Entity type (optional)</label>
              <input
                className="ednl-input"
                value={form.entity_type}
                onChange={(e) => updateField('entity_type', e.target.value)}
                placeholder="e.g. Customer, Account — used for cross-source unification"
                disabled={saving}
              />
            </div>
            <div style={{ gridColumn: '1 / span 2' }}>
              <label className="ednl-label">Business description</label>
              <textarea
                className="ednl-textarea"
                rows={3}
                style={{ width: '100%' }}
                value={form.business_description}
                onChange={(e) => updateField('business_description', e.target.value)}
                placeholder="Plain-English meaning used by the ontology and exported markdown"
                disabled={saving}
              />
            </div>
          </div>

          {showVocab && (
            <div style={{ marginTop: 12 }}>
              <label className="ednl-label">Value vocabulary</label>
              <p className="ednl-hint" style={{ marginTop: 2 }}>
                For status/category columns, map each raw value to a human-readable meaning.
              </p>
              <table className="ednl-table" style={{ marginTop: 6 }}>
                <thead>
                  <tr>
                    <th style={{ width: '30%' }}>Code</th>
                    <th>Meaning</th>
                    <th style={{ width: 60 }} />
                  </tr>
                </thead>
                <tbody>
                  {(form.vocab_rows || []).map((row, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          className="ednl-input"
                          value={row.key}
                          onChange={(e) => updateVocabRow(idx, 'key', e.target.value)}
                          placeholder="e.g. A"
                          disabled={saving}
                        />
                      </td>
                      <td>
                        <input
                          className="ednl-input"
                          value={row.value}
                          onChange={(e) => updateVocabRow(idx, 'value', e.target.value)}
                          placeholder="e.g. Active"
                          disabled={saving}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="ednl-btn ednl-btn--sm"
                          onClick={() => removeVocabRow(idx)}
                          disabled={saving}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                className="ednl-btn ednl-btn--sm"
                onClick={addVocabRow}
                disabled={saving}
                style={{ marginTop: 6 }}
              >
                + Add vocabulary row
              </button>
            </div>
          )}

          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="ednl-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="ednl-btn ednl-btn--primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save tag'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SchemaViewer({ data, onTagsUpdated }) {
  const schema = data.schema || {};
  const tags = data.tags || [];
  const tables = schema.tables || [];
  const sourceId = data.source_id || schema.source_id || (tags[0] && tags[0].source_id) || '';

  const tagsByField = {};
  tags.forEach((t) => { tagsByField[t.field_path] = t; });

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editorError, setEditorError] = useState(null);

  const openEditor = (fieldPath, col) => {
    const tag = tagsByField[fieldPath];
    setEditorError(null);
    setEditing({
      columnName: col.name,
      form: _tagToEditState(tag, col, fieldPath, sourceId),
    });
  };

  const closeEditor = () => {
    if (saving) return;
    setEditing(null);
    setEditorError(null);
  };

  const saveEditor = async (payload) => {
    if (!sourceId) {
      setEditorError('Source id missing — please re-open View Schema and try again.');
      return;
    }
    setSaving(true);
    setEditorError(null);
    try {
      await sourcesApi.updateTag(sourceId, payload);
      setSaving(false);
      setEditing(null);
      if (typeof onTagsUpdated === 'function') {
        await onTagsUpdated(sourceId);
      }
    } catch (err) {
      setSaving(false);
      const detail = err?.response?.data?.detail;
      setEditorError(typeof detail === 'string' ? detail : err.message || 'Failed to save tag');
    }
  };

  return (
    <div>
      <p style={{ marginBottom: 8, fontWeight: 600 }}>
        {tables.length} table(s), {tags.length} semantic tag(s)
      </p>
      {tables.map((tbl) => {
        const prefix = tbl.schema_name ? `${tbl.schema_name}.${tbl.table_name}` : tbl.table_name;
        return (
          <div key={prefix} className="ednl-panel" style={{ marginBottom: 8 }}>
            <div className="ednl-panel__header">
              {prefix} ({tbl.columns?.length || 0} columns, ~{tbl.row_count_estimate ?? '?'} rows)
            </div>
            <div className="ednl-panel__body">
              <table className="ednl-table">
                <thead>
                  <tr>
                    <th>Column</th>
                    <th>Type</th>
                    <th>PK</th>
                    <th>FK</th>
                    <th>Business Name</th>
                    <th>Semantic Type</th>
                    <th>Narrative Role</th>
                    <th>Description</th>
                    <th style={{ width: 90 }}>Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {(tbl.columns || []).map((col) => {
                    const fp = `${prefix}.${col.name}`;
                    const tag = tagsByField[fp];
                    const confirmed = tag?.is_user_confirmed === true;
                    const desc = tag?.business_description || '';
                    return (
                      <tr key={col.name}>
                        <td className="mono">
                          {col.name}
                          {confirmed && (
                            <span
                              title="User-confirmed tag (locked against future auto-infer)"
                              style={{ marginLeft: 6, color: '#059669', fontWeight: 700 }}
                            >
                              ✓
                            </span>
                          )}
                        </td>
                        <td className="mono">{col.data_type}</td>
                        <td>{col.is_primary_key ? '✓' : ''}</td>
                        <td>{col.is_foreign_key ? `→ ${col.fk_target_table}` : ''}</td>
                        <td>{tag?.business_name || <span style={{ color: '#9ca3af' }}>—</span>}</td>
                        <td>{tag ? <StatusBadge status="info" label={tag.semantic_type} /> : '—'}</td>
                        <td>{tag?.narrative_role || '—'}</td>
                        <td style={{ maxWidth: 280, fontSize: 12, color: '#4b5563' }}>
                          {desc
                            ? (desc.length > 120 ? `${desc.slice(0, 117)}…` : desc)
                            : <span style={{ color: '#9ca3af' }}>—</span>}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="ednl-btn ednl-btn--sm"
                            onClick={() => openEditor(fp, col)}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
      <JsonViewer data={schema.source_metadata} label="Source metadata" />

      {editing && (
        <TagEditorModal
          initial={editing.form}
          columnName={editing.columnName}
          onClose={closeEditor}
          onSave={saveEditor}
          saving={saving}
          error={editorError}
        />
      )}
    </div>
  );
}
